import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

export interface CommissionSettings {
  store: number | null;
  groAmount: number;
  rgAmount: number;
  readerAmount: number;
}

export interface CommissionSale {
  id: string;
  date: string;
  model: string;
  color: string;
  eyeSize: string;
  patientLastName: string;
  category: 'gro' | 'rg' | 'spiff' | 'reader';
  amount: number;
}

export interface CommissionCounts {
  gro: number;
  rg: number;
  spiff: number;
  reader: number;
  exam: number;
}

export interface CommissionEmployee {
  id: string;
  initials: string;
  name: string;
  sales: CommissionSale[];
  counts: CommissionCounts;
  dollars: CommissionCounts;
  total: number;
  salesCount: number;
}

export interface CommissionHistory {
  id: string;
  store: number;
  openedAt: string;
  closedAt: string;
  paidAt: string;
  paidByName: string;
  total: number;
  adjustedTotal: number;
  locked: boolean;
}

export interface CommissionAdjustment {
  id: string;
  model: string;
  patientLastName: string;
  amount: number;
  saleDate: string;
  reason: { reason?: string; byName?: string; at?: string } | null;
}

export interface CommissionUser {
  id: string;
  initials: string;
  name: string;
}

export interface CommissionSheet {
  _id?: string;
  status?: string;
  openedAt?: string;
  paidAt?: string;
  paidByName?: string;
  paidTotal?: number;
  adjustedTotal?: number;
}

export interface CommissionReport {
  success: boolean;
  message?: string;
  settings: CommissionSettings;
  sheet: CommissionSheet | null;
  open: boolean;
  employees: CommissionEmployee[];
  adjustments: CommissionAdjustment[];
  history: CommissionHistory[];
  users: CommissionUser[];
}

@Injectable({ providedIn: 'root' })
export class CommissionService {
  private baseUrl = environment.base_url + '/commission';

  constructor(private http: HttpClient) {}

  getReport(query: {
    store: string;
    view: string;
    year: number;
    sheet?: string;
    settingsStore?: string;
  }): Observable<CommissionReport> {
    let params = new HttpParams()
      .set('store', query.store || 'all')
      .set('view', query.view || 'open')
      .set('year', String(query.year));
    if (query.sheet) {
      params = params.set('sheet', query.sheet);
    }
    if (query.settingsStore) {
      params = params.set('settingsStore', query.settingsStore);
    }
    return this.http.get<CommissionReport>(this.baseUrl, { params });
  }

  saveSettings(settings: CommissionSettings): Observable<{ success: boolean; message?: string }> {
    return this.http.put<{ success: boolean; message?: string }>(
      `${this.baseUrl}/settings`,
      settings
    );
  }

  markPaid(store: string): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.baseUrl}/pay`, {
      store,
    });
  }

  reassign(
    entryId: string,
    employeeId: string,
    reason: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.baseUrl}/entries/${entryId}/reassign`,
      { employeeId, reason }
    );
  }
}
